import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/admin/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useFleetAlerts, useAcknowledgeAlert, useResolveAlert, FleetAlert } from "@/hooks/use-fleet-alerts";
import { getAlertTypeLabel, getPriorityLabel, getStatusLabel } from "@/lib/fleet/labels";
import { 
  AlertTriangle, 
  Bell, 
  CheckCircle, 
  Clock, 
  Settings, 
  Eye,
  X,
  Wrench,
  FileText,
  Calendar,
  Fuel,
  MoreHorizontal,
  Radio
} from "lucide-react";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GpsAlertsTable } from "@/components/fleet/GpsAlertsTable";
import { useUnifiedAlerts } from "@/hooks/useUnifiedAlerts";
import { useHasGpsTracking } from "@/hooks/useHasGpsTracking";

// Using FleetAlert interface from hook

const alertTypes = [
  { value: "maintenance", label: "Manutenção", icon: Wrench },
  { value: "document", label: "Documento", icon: FileText },
  { value: "fuel", label: "Combustível", icon: Fuel },
  { value: "violation", label: "Infração", icon: AlertTriangle },
  { value: "system", label: "Sistema", icon: Settings },
];

const priorityColors = {
  low: "default",
  medium: "secondary",
  high: "destructive",
  critical: "destructive"
} as const;

export default function FleetAlerts() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [selectedAlert, setSelectedAlert] = useState<FleetAlert | null>(null);
  const [activeTab, setActiveTab] = useState("all");

  // Get current user's company
  const { data: userCompanies } = useQuery({
    queryKey: ["userCompanies"],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_companies")
        .select("company_id, companies(name)")
        .eq("user_id", profile?.id);
      return data || [];
    },
    enabled: !!profile?.id
  });

  const currentCompanyId = userCompanies?.[0]?.company_id;

  // Check if company has GPS tracking
  const { hasGps } = useHasGpsTracking(currentCompanyId);

  // Get unified alerts data (Fleet + GPS)
  const unifiedAlerts = useUnifiedAlerts(currentCompanyId);
  const alerts = unifiedAlerts.fleetAlerts;
  
  const acknowledgeAlert = useAcknowledgeAlert(currentCompanyId!);
  const resolveAlert = useResolveAlert(currentCompanyId!);

  const handleAcknowledge = (alertId: string) => {
    acknowledgeAlert.mutate(alertId);
  };

  const handleResolve = (alertId: string) => {
    resolveAlert.mutate(alertId);
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      active: "destructive",
      acknowledged: "secondary",
      resolved: "default"
    } as const;
    
    const variant = statusMap[status as keyof typeof statusMap] || "outline";
    return <Badge variant={variant}>{getStatusLabel(status)}</Badge>;
  };

  const getPriorityBadgeVariant = (priority: string) => {
    const variant = priorityColors[priority as keyof typeof priorityColors] || "default";
    return <Badge variant={variant}>{getPriorityLabel(priority)}</Badge>;
  };

  const getTypeIcon = (type: string) => {
    const alertType = alertTypes.find(t => t.value === type);
    const IconComponent = alertType?.icon || AlertTriangle;
    return <IconComponent className="h-4 w-4" />;
  };

  const columns: ColumnDef<FleetAlert>[] = [
    {
      accessorKey: "title",
      header: "Título",
    },
    {
      accessorKey: "alert_type",
      header: "Tipo",
      cell: ({ row }) => {
        const type = alertTypes.find(t => t.value === row.getValue("alert_type"));
        const IconComponent = type?.icon || AlertTriangle;
        const alertType = row.getValue("alert_type") as string;
        return (
          <div className="flex items-center gap-2">
            <IconComponent className="h-4 w-4" />
            <span>{getAlertTypeLabel(alertType)}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "priority",
      header: "Prioridade",
      cell: ({ row }) => {
        return getPriorityBadgeVariant(row.getValue("priority"));
      },
    },
    {
      header: "Entidade",
      cell: ({ row }) => {
        const alert = row.original;
        if (alert.fleet_vehicles) {
          return (
            <div className="flex items-center gap-2">
              <Badge variant="outline">Veículo</Badge>
              <span className="text-sm">{alert.fleet_vehicles.plate}</span>
            </div>
          );
        } else if (alert.fleet_drivers) {
          return (
            <div className="flex items-center gap-2">
              <Badge variant="outline">Motorista</Badge>
              <span className="text-sm">{alert.fleet_drivers.name}</span>
            </div>
          );
        }
        return <Badge variant="outline">Sistema</Badge>;
      },
    },
    {
      accessorKey: "triggered_at",
      header: "Data",
      cell: ({ row }) => {
        return new Date(row.getValue("triggered_at")).toLocaleDateString("pt-BR");
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        return getStatusBadge(row.getValue("status"));
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const alert = row.original;
        
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Abrir menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSelectedAlert(alert)}>
                <Eye className="mr-2 h-4 w-4" />
                Ver detalhes
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleAcknowledge(alert.id)}
                disabled={alert.status !== 'active'}
              >
                Reconhecer
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleResolve(alert.id)}
                disabled={alert.status === 'resolved'}
              >
                Resolver
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  // Filter alerts based on active tab
  const filteredAlerts = alerts.filter(alert => {
    if (activeTab === "all") return true;
    if (activeTab === "active") return alert.status === "active";
    if (activeTab === "acknowledged") return alert.status === "acknowledged";
    if (activeTab === "resolved") return alert.status === "resolved";
    return alert.alert_type === activeTab;
  });

  // Calculate summary data
  const totalAlerts = alerts.length;
  const activeAlerts = alerts.filter(alert => alert.status === "active").length;
  const criticalAlerts = alerts.filter(alert => alert.priority === "critical").length;
  const highPriorityAlerts = alerts.filter(alert => alert.priority === "high").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Alertas da Frota</h1>
          <p className="text-muted-foreground">
            Monitore alertas e notificações da frota
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalAlerts}</div>
            <p className="text-xs text-muted-foreground">
              alertas registrados
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ativos</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeAlerts}</div>
            <p className="text-xs text-muted-foreground">
              alertas ativos
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Críticos</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-800" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{criticalAlerts}</div>
            <p className="text-xs text-muted-foreground">
              prioridade crítica
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Alta Prioridade</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{highPriorityAlerts}</div>
            <p className="text-xs text-muted-foreground">
              prioridade alta
            </p>
          </CardContent>
        </Card>
        {hasGps && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Alertas GPS</CardTitle>
              <Radio className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{unifiedAlerts.gpsTotal}</div>
              <p className="text-xs text-muted-foreground">
                {unifiedAlerts.gpsUnresolved} não resolvidos
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Alerts Table */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">Todos</TabsTrigger>
          <TabsTrigger value="active">Ativos</TabsTrigger>
          <TabsTrigger value="acknowledged">Reconhecidos</TabsTrigger>
          <TabsTrigger value="resolved">Resolvidos</TabsTrigger>
          <TabsTrigger value="maintenance">Manutenção</TabsTrigger>
          <TabsTrigger value="document">Documentos</TabsTrigger>
          <TabsTrigger value="fuel">Combustível</TabsTrigger>
          {hasGps && (
            <TabsTrigger value="gps">
              <Radio className="h-4 w-4 mr-2" />
              Alertas GPS
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value={activeTab} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Alertas</CardTitle>
              <CardDescription>
                Lista de alertas da frota
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable 
                columns={columns} 
                data={filteredAlerts}
                searchKey="title"
              />
            </CardContent>
          </Card>
        </TabsContent>
        {hasGps && (
          <TabsContent value="gps" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Radio className="h-5 w-5 text-green-600" />
                  Alertas do Sistema GPS
                </CardTitle>
                <CardDescription>
                  Alertas de velocidade, geocerca, bateria e dispositivos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <GpsAlertsTable companyId={currentCompanyId!} />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}